import { Fragment, useEffect, useMemo, useRef } from "react";
import type {
  Assignment,
  MapDefinition,
  Point,
  Tactic,
  Utility,
} from "@cs2/contracts";
import { isPlacedUtility } from "@cs2/physics";
import Konva from "konva";
import { Circle, Group, Layer, Line, Rect, Stage, Text } from "react-konva";
import { Box, ChevronDown } from "lucide-react";
import { useWalkabilityGrid } from "./useNavigationAsset.js";
import { pathAnimationState, pointAlongPath } from "../lib/path-animation.js";
import {
  glyphScale,
  overlay,
  utilityColor,
  mapThemeColor,
} from "../lib/map-style.js";
import { pointAtUtilityTrajectory } from "@cs2/physics";
import {
  bloomDurationMs,
  throwDurationMs,
  trajectoryAnimationState,
} from "../lib/trajectory-animation.js";
import {
  BombMarker,
  CalloutLayer,
  MapBackgroundLayer,
  ThrownUtilityLanding,
  useImage,
} from "./konva-marks.js";
import {
  getArrowGeometry,
  getHighlightGeometry,
  getPointGeometry,
  stepOpacity as sharedStepOpacity,
} from "./scene.js";
import { useOwnWalkPath, useTeamWalkPaths } from "./useWalkPaths.js";
import { useThrownUtilityGeometry } from "./useThrownUtilityGeometry.js";
import { useStageSize } from "./useStageSize.js";
import { isMapPlacementButton } from "./map-zoom.js";
import { MapZoomControls } from "./MapZoomControls.js";
import { useMapZoomPan } from "./useMapZoomPan.js";
import {
  OwnRoute,
  RouteStepMarkers,
  TeamBadges,
  TeamRouteLines,
} from "./route-layers.js";
import "./map.css";

/** Auswählen ist optional; Quickview und Platzierungsansichten bleiben passiv. */
function utilitySelectionHandlers(onSelect?: () => void) {
  if (!onSelect) return {};
  return {
    onClick: (event: Konva.KonvaEventObject<MouseEvent>) => {
      if (!isMapPlacementButton(event.evt.button)) return;
      event.cancelBubble = true;
      onSelect();
    },
    onTap: (event: Konva.KonvaEventObject<Event>) => {
      event.cancelBubble = true;
      onSelect();
    },
  };
}

/**
 * Gemeinsamer read-only Karten-Renderer (Konva) für die Quickview — derselbe
 * Stack wie der Editor (`EditorCanvas`), damit sich die Karte nie in Mathematik
 * oder Look unterscheidet (Hard Rule 2). Die Stage ist quadratisch (kürzere
 * Panelkante), damit die 0–1000-Karte nicht verzerrt.
 */

/**
 * Eine geworfene Utility inkl. Wurf-Animation. Wird `animate` wahr (Schritt-
 * Wiedergabe erreicht den verknüpften Schritt), fliegt der Token entlang des
 * Bogens und die Wirkfläche blüht auf; sonst der statische Endzustand.
 */
function ThrownUtility({
  utility,
  color,
  opacity,
  scale,
  sx,
  sy,
  animate,
  onSelect,
}: {
  utility: Utility;
  color: string;
  opacity: number;
  scale: number;
  sx: number;
  sy: number;
  animate: boolean;
  onSelect?: (() => void) | undefined;
}) {
  const projectileRef = useRef<Konva.Circle>(null);
  const effectRef = useRef<Konva.Group>(null);
  const { trajectory, landing, effectAreas } =
    useThrownUtilityGeometry(utility);

  useEffect(() => {
    const projectile = projectileRef.current;
    const effect = effectRef.current;
    if (!animate || !projectile || !effect) return;
    effect.opacity(0);
    effect.scale({ x: 0.25, y: 0.25 });
    projectile.opacity(1);
    const anim = new Konva.Animation((frame) => {
      const elapsed = frame ? frame.time : 0;
      const animation = trajectoryAnimationState(
        elapsed,
        throwDurationMs,
        bloomDurationMs,
      );
      const point = pointAtUtilityTrajectory(
        trajectory,
        animation.flightProgress,
      );
      projectile.position({ x: point.x * sx, y: point.y * sy });
      if (animation.flightProgress >= 1) {
        projectile.opacity(0);
        effect.opacity(animation.bloomProgress);
        effect.scale({
          x: 0.25 + 0.75 * animation.bloomProgress,
          y: 0.25 + 0.75 * animation.bloomProgress,
        });
        if (animation.finished) anim.stop();
      }
    }, projectile.getLayer());
    anim.start();
    return () => {
      anim.stop();
      effect.opacity(1);
      effect.scale({ x: 1, y: 1 });
      projectile.opacity(0);
    };
  }, [animate, sx, sy, trajectory]);

  return (
    <Fragment>
      <ThrownUtilityLanding
        utility={utility}
        color={color}
        scale={scale}
        sx={sx}
        sy={sy}
        trajectory={trajectory}
        landing={landing}
        areas={effectAreas}
        opacity={opacity}
        groupRef={effectRef}
        interactive={Boolean(onSelect)}
        {...utilitySelectionHandlers(onSelect)}
      />
      <Circle
        ref={projectileRef}
        x={utility.start.x * sx}
        y={utility.start.y * sy}
        radius={overlay.routeDot.radius * scale}
        fill={color}
        opacity={0}
        listening={false}
      />
    </Fragment>
  );
}

/**
 * Spieler-Marker für die Schritt-Wiedergabe. Der Marker folgt jedem Punkt des
 * berechneten A*-Segments nach Entfernung, damit Kurven und Ausweichwege als
 * echtes A-nach-B-Pathing sichtbar werden.
 */
function WalkingPlayer({
  path,
  durationMs,
  player,
  scale,
  sx,
  sy,
}: {
  path: readonly Point[];
  durationMs: number;
  player: string;
  scale: number;
  sx: number;
  sy: number;
}) {
  const walkerRef = useRef<Konva.Group>(null);
  const start = useMemo(() => pointAlongPath(path, 0) ?? path[0]!, [path]);

  useEffect(() => {
    const walker = walkerRef.current;
    if (!walker || path.length < 2) return;
    const position = (point: Point) =>
      walker.position({ x: point.x * sx, y: point.y * sy });
    position(start);

    const layer = walker.getLayer();
    if (!layer) return;
    const animation = new Konva.Animation((frame) => {
      const state = pathAnimationState(frame?.time ?? 0, durationMs);
      const point = pointAlongPath(path, state.progress);
      if (point) position(point);
      if (state.finished) animation.stop();
    }, layer);
    animation.start();
    return () => {
      animation.stop();
    };
  }, [durationMs, path, start, sx, sy]);

  return (
    <Group ref={walkerRef} x={start.x * sx} y={start.y * sy} listening={false}>
      <Circle
        radius={overlay.teamPlayer.radius * 1.15 * scale}
        fill="#101719"
        stroke={mapThemeColor("--route")}
        strokeWidth={overlay.teamPlayer.stroke * 1.5 * scale}
        shadowBlur={0}
        shadowColor={mapThemeColor("--route")}
      />
      <Text
        text={player}
        width={overlay.teamPlayer.radius * 2.3 * scale}
        height={overlay.teamPlayer.radius * 2.3 * scale}
        x={-overlay.teamPlayer.radius * 1.15 * scale}
        y={-overlay.teamPlayer.radius * 1.15 * scale}
        align="center"
        verticalAlign="middle"
        fill={mapThemeColor("--route")}
        fontStyle="bold"
        fontSize={overlay.teamPlayer.fontSize * scale}
      />
    </Group>
  );
}

export function MapView({
  map,
  tactic,
  assignment,
  utilities,
  player,
  legendOpen,
  onToggleLegend,
  selectedStepOrder,
  selectedUtilityId = null,
  onMapPositionClick,
  onUtilitySelect,
  hideChrome = false,
  zoomable = false,
  playingStepOrder = null,
  playingStepDurationMs = null,
  utilityStepOrder = null,
  stageRef,
}: {
  map: MapDefinition;
  tactic: Tactic;
  assignment: Assignment;
  utilities: Utility[];
  player: string;
  legendOpen: boolean;
  onToggleLegend: () => void;
  selectedStepOrder: number | null;
  selectedUtilityId?: string | null;
  onMapPositionClick?: ((point: Point) => void) | undefined;
  onUtilitySelect?: ((id: string) => void) | undefined;
  hideChrome?: boolean;
  zoomable?: boolean;
  playingStepOrder?: number | null;
  playingStepDurationMs?: number | null;
  utilityStepOrder?: number | null;
  stageRef?: React.RefObject<Konva.Stage | null>;
}) {
  const { containerRef: container, size } = useStageSize("square", {
    width: 0,
    height: 0,
  });
  const side = size.width;
  const image = useImage(map.radarPath ? `/${map.radarPath}` : null);
  const { mapZoom, renderZoom, applyMapZoom, scrollProps, contentProps } =
    useMapZoomPan({ side, enabled: zoomable, resetKey: map.id });
  const renderSide = side * renderZoom;
  const sx = renderSide / 1000;
  const sy = renderSide / 1000;
  const scale = glyphScale(renderSide);
  const walkabilityGrid = useWalkabilityGrid(map.id);
  const personalPathResult = useOwnWalkPath(walkabilityGrid, assignment.route);
  const teamWalkResults = useTeamWalkPaths(
    walkabilityGrid,
    tactic.assignments,
    player,
  );
  const visibleElements = tactic.elements.filter(
    (element) => element.slot === null || element.slot === player,
  );
  const numberedCount = Math.min(
    assignment.route.length,
    assignment.steps.length,
  );
  const activeStepIndex =
    playingStepOrder === null
      ? -1
      : assignment.steps.findIndex((step) => step.order === playingStepOrder);
  const activeWalkingPath =
    activeStepIndex > 0
      ? (personalPathResult.segments[activeStepIndex - 1]?.points ?? null)
      : null;
  function stepOpacity(stepOrder: number | null) {
    return sharedStepOpacity(stepOrder, selectedStepOrder, 0.3);
  }
  function utilityOpacity(utility: Utility) {
    const byStep = stepOpacity(assignment.utilityStepLinks[utility.id] ?? null);
    if (!selectedUtilityId) return byStep;
    // Im auswählbaren Katalog bleiben auch die übrigen Treffer gut sichtbar.
    return utility.id === selectedUtilityId
      ? 1
      : Math.min(byStep, onUtilitySelect ? 0.65 : 0.2);
  }

  return (
    <section className="qv-map-panel" aria-label="Taktikkarte">
      <div className="qv-map-stage" ref={container}>
        {!image ? (
          <div className="qv-map-empty">RADAR SETUP AUSSTEHEND</div>
        ) : null}
        <div {...scrollProps}>
          <div {...contentProps}>
            {side > 0 ? (
              <div
                className="qv-map-square"
                style={{ width: renderSide, height: renderSide }}
              >
                <Stage
                  width={renderSide}
                  height={renderSide}
                  ref={stageRef}
                  {...(onMapPositionClick
                    ? {
                        onClick: (event) => {
                          // Konva feuert `click` für jede Maustaste; der
                          // Mittelklick verschiebt die Karte und setzt nichts.
                          if (!isMapPlacementButton(event.evt.button)) return;
                          const point = event.target
                            .getStage()
                            ?.getPointerPosition();
                          if (!point || sx === 0 || sy === 0) return;
                          onMapPositionClick({
                            x: Math.max(0, Math.min(1000, point.x / sx)),
                            y: Math.max(0, Math.min(1000, point.y / sy)),
                          });
                        },
                      }
                    : {})}
                >
                  <MapBackgroundLayer
                    image={image}
                    width={renderSide}
                    height={renderSide}
                  />
                  <CalloutLayer
                    callouts={map.callouts}
                    sx={sx}
                    sy={sy}
                    scale={scale}
                  />
                  <Layer listening={Boolean(onUtilitySelect)}>
                    <TeamRouteLines
                      assignments={tactic.assignments}
                      excludeSlot={player}
                      walkPaths={teamWalkResults}
                      sx={sx}
                      sy={sy}
                      scale={scale}
                    />
                    <OwnRoute
                      path={personalPathResult}
                      blockedPoint={
                        personalPathResult.blockedAt !== null
                          ? (assignment.route[personalPathResult.blockedAt] ??
                            null)
                          : null
                      }
                      sx={sx}
                      sy={sy}
                      scale={scale}
                    />
                    <TeamBadges
                      assignments={tactic.assignments}
                      excludeSlot={player}
                      sx={sx}
                      sy={sy}
                      scale={scale}
                    />
                    <RouteStepMarkers
                      route={assignment.route}
                      numberedCount={numberedCount}
                      sx={sx}
                      sy={sy}
                      scale={scale}
                    />
                    {activeWalkingPath && playingStepDurationMs ? (
                      <WalkingPlayer
                        path={activeWalkingPath}
                        durationMs={playingStepDurationMs}
                        player={player}
                        scale={scale}
                        sx={sx}
                        sy={sy}
                      />
                    ) : null}
                    {utilities.map((utility) => {
                      const color = utilityColor(utility.type);
                      const opacity = utilityOpacity(utility);
                      if (isPlacedUtility(utility))
                        return (
                          <Group
                            key={utility.id}
                            x={utility.target.x * sx}
                            y={utility.target.y * sy}
                            opacity={opacity}
                            {...utilitySelectionHandlers(
                              onUtilitySelect
                                ? () => onUtilitySelect(utility.id)
                                : undefined,
                            )}
                          >
                            {onUtilitySelect ? (
                              <Circle
                                radius={24 * scale}
                                fill="rgba(0,0,0,0.001)"
                              />
                            ) : null}
                            <BombMarker
                              color={color}
                              caption={`${tactic.side} · ${utility.targetCallout}`}
                              scale={scale}
                            />
                          </Group>
                        );
                      return (
                        <ThrownUtility
                          key={utility.id}
                          utility={utility}
                          onSelect={
                            onUtilitySelect
                              ? () => onUtilitySelect(utility.id)
                              : undefined
                          }
                          color={color}
                          opacity={opacity}
                          scale={scale}
                          sx={sx}
                          sy={sy}
                          animate={
                            utilityStepOrder !== null &&
                            (assignment.utilityStepLinks[utility.id] ??
                              null) === utilityStepOrder
                          }
                        />
                      );
                    })}
                    {visibleElements.map((element) => {
                      const opacity = stepOpacity(element.stepOrder);
                      if (element.type === "text") {
                        const g = getPointGeometry(element);
                        return (
                          <Text
                            key={element.id}
                            x={g.x * sx}
                            y={g.y * sy}
                            text={element.label ?? ""}
                            fill="#e8ebe8"
                            fontStyle="bold"
                            fontSize={overlay.element.textFontSize * scale}
                            opacity={opacity}
                          />
                        );
                      }
                      if (element.type === "arrow") {
                        const g = getArrowGeometry(element);
                        return (
                          <Line
                            key={element.id}
                            points={[
                              g.x1 * sx,
                              g.y1 * sy,
                              g.x2 * sx,
                              g.y2 * sy,
                            ]}
                            stroke="#e8ebe8"
                            strokeWidth={overlay.element.arrowStroke * scale}
                            opacity={opacity}
                          />
                        );
                      }
                      if (element.type === "highlight_area") {
                        const g = getHighlightGeometry(element);
                        return (
                          <Rect
                            key={element.id}
                            x={g.x * sx}
                            y={g.y * sy}
                            width={g.width * sx}
                            height={g.height * sy}
                            fill="rgba(242,170,0,.12)"
                            stroke="#e8ebe8"
                            strokeWidth={
                              overlay.element.highlightStroke * scale
                            }
                            dash={overlay.element.highlightDash.map(
                              (v) => v * scale,
                            )}
                            opacity={opacity}
                          />
                        );
                      }
                      return null;
                    })}
                  </Layer>
                </Stage>
              </div>
            ) : null}
          </div>
        </div>
        {zoomable ? (
          <MapZoomControls mapZoom={mapZoom} onZoom={applyMapZoom} />
        ) : null}
        {hideChrome ? null : (
          <div className={`qv-legend${legendOpen ? "" : " collapsed"}`}>
            <button
              type="button"
              className="qv-legend-toggle"
              onClick={onToggleLegend}
              aria-expanded={legendOpen}
              aria-label="Legende"
            >
              {legendOpen ? (
                <>
                  <h2>LEGENDE</h2>
                  <ChevronDown />
                </>
              ) : (
                <>
                  <Box />
                  <span>Legende</span>
                </>
              )}
            </button>
            {legendOpen ? (
              <div className="qv-legend-body">
                <div>
                  <i className="legend-dot personal" /> ICH ({player})
                </div>
                <div>
                  <i className="legend-dot" /> TEAM
                </div>
                <div>
                  <i className="legend-line" /> ROUTE
                </div>
                <div>
                  <i className="legend-target" /> UTILITY LANDUNG
                </div>
                <div>
                  <i className="legend-area" /> EFFEKT BEREICH
                </div>
              </div>
            ) : null}
          </div>
        )}
        {hideChrome ? null : (
          <div className="qv-caption">
            <b>{tactic.side}</b>
            <span>
              <small>{tactic.category}</small>
              <strong>{tactic.name}</strong>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
